import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';

import { ExpandingAreaWithReadMore } from 'dibs-elements/exports/ExpandingArea';
import { PdpSharedContactDealerButton } from '../PdpSharedButtons/PdpSharedContactDealerButton';

import styles from './PdpSharedDescription.scss';

export const Title = ({ isJewelry }) => {
    const defaultTitle = (
        <FormattedMessage
            id="PdpSharedDescriptionAdditionalOptions.customizationHeader"
            defaultMessage="Customization Options"
        />
    );

    const jewelryTitle = (
        <FormattedMessage
            id="PdpSharedDescriptionAdditionalOptions.customizationHeaderJewelry"
            defaultMessage="Custom Services"
        />
    );

    return isJewelry ? jewelryTitle : defaultTitle;
};

const PdpSharedDescriptionAdditionalOptionsContentComponent = ({
    customizationOptions,
    contentClass,
    item,
    user,
    viewer,
    isFullWidthCta = false,
    buttonType,
    collapsedHeight,
    disableExpansion,
}) => {
    const [contentHeight, setContentHeight] = useState(null);
    const setContentRef = useCallback(node => {
        if (node) {
            setContentHeight(node.getBoundingClientRect().height);
        }
    }, []);

    const COLLAPSED_CONTENT_HEIGHT = collapsedHeight || 120;

    const shouldBeExpandable = !disableExpansion && contentHeight >= COLLAPSED_CONTENT_HEIGHT;
    const showProductionTime =
        item?.seller?.isJewelrySeller && item?.isCustomizable && item?.leadTimeDisplay;

    const content = (
        <span className={classNames(styles.content, contentClass)} ref={setContentRef}>
            {customizationOptions}
            {showProductionTime && (
                <div>
                    <span className={styles.productionTime}>
                        <FormattedMessage
                            id="PdpSharedDescriptionAdditionalOptions.productionTime"
                            defaultMessage="Estimated Production Time"
                        />
                    </span>
                    {': '}
                    {item.leadTimeDisplay}
                </div>
            )}
        </span>
    );

    const contactButton = (
        <div className={styles.ctaWrapper}>
            <PdpSharedContactDealerButton
                size="small"
                fullWidth={isFullWidthCta}
                buttonType={buttonType || 'secondary'}
                type={'REQUEST_CUSTOMIZATION'}
                item={item}
                user={user}
                viewer={viewer}
            />
        </div>
    );
    return shouldBeExpandable ? (
        <>
            <ExpandingAreaWithReadMore
                collapsedHeight={COLLAPSED_CONTENT_HEIGHT}
                showGradient={false}
            >
                {content}
            </ExpandingAreaWithReadMore>
            {contactButton}
        </>
    ) : (
        <>
            {content}
            {contactButton}
        </>
    );
};

PdpSharedDescriptionAdditionalOptionsContentComponent.propTypes = {
    customizationOptions: PropTypes.string,
    contentClass: PropTypes.string,
    item: PropTypes.object,
    user: PropTypes.object,
    viewer: PropTypes.object,
    isFullWidthCta: PropTypes.bool,
    buttonType: PropTypes.string,
    collapsedHeight: PropTypes.number,
    disableExpansion: PropTypes.bool,
};

export const PdpSharedDescriptionAdditionalOptionsContent = createFragmentContainer(
    PdpSharedDescriptionAdditionalOptionsContentComponent,
    {
        user: graphql`
            fragment PdpSharedDescriptionAdditionalOptionsContent_user on User {
                ...PdpSharedContactDealerButton_user
            }
        `,
        item: graphql`
            fragment PdpSharedDescriptionAdditionalOptionsContent_item on Item {
                ...PdpSharedContactDealerButton_item
                leadTimeDisplay
                isCustomizable
                seller {
                    isJewelrySeller
                }
            }
        `,
        viewer: graphql`
            fragment PdpSharedDescriptionAdditionalOptionsContent_viewer on Viewer {
                ...PdpSharedContactDealerButton_viewer
            }
        `,
    }
);
